import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

export const SignInStaggeredMenu = ({
  position = 'right',
  colors = ['#0c6749ff', '#073A29'],
  items = [],
  socialItems = [],
  displaySocials = true,
  displayItemNumbering = true,
  accentColor = '#073A29',
  onMenuOpen,
  onMenuClose,
  onContactUs
}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSignUp, setIsSignUp] = useState(false);
  const [signUpData, setSignUpData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();

  const panelRef = useRef(null);
  const preLayersRef = useRef(null);
  const preLayerElsRef = useRef([]);

  const openTlRef = useRef(null);
  const closeTweenRef = useRef(null);
  const busyRef = useRef(false);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const panel = panelRef.current;
      const preContainer = preLayersRef.current;

      if (!panel || !preContainer) return;

      let preLayers = [];
      if (preContainer) {
        preLayers = Array.from(preContainer.querySelectorAll('.sm-prelayer'));
      }
      preLayerElsRef.current = preLayers;

      const offscreen = 100;
      gsap.set([panel, ...preLayers], { xPercent: offscreen });
    });
    return () => ctx.revert();
  }, []);

  const buildOpenTimeline = useCallback(() => {
    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return null;

    openTlRef.current?.kill();
    if (closeTweenRef.current) {
      closeTweenRef.current.kill();
      closeTweenRef.current = null;
    }

    const itemEls = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
    const numberEls = Array.from(panel.querySelectorAll('.sm-panel-list[data-numbering] .sm-panel-item'));
    const socialTitle = panel.querySelector('.sm-socials-title');
    const socialLinks = Array.from(panel.querySelectorAll('.sm-socials-link'));
    const formElements = Array.from(panel.querySelectorAll('.form-element'));

    const layerStates = layers.map(el => ({ el, start: Number(gsap.getProperty(el, 'xPercent')) }));
    const panelStart = Number(gsap.getProperty(panel, 'xPercent'));

    if (itemEls.length) gsap.set(itemEls, { yPercent: 140, rotate: 10 });
    if (numberEls.length) gsap.set(numberEls, { '--sm-num-opacity': 0 });
    if (socialTitle) gsap.set(socialTitle, { opacity: 0 });
    if (socialLinks.length) gsap.set(socialLinks, { y: 25, opacity: 0 });
    if (formElements.length) gsap.set(formElements, { y: 30, opacity: 0 });

    const tl = gsap.timeline({ paused: true });

    layerStates.forEach((ls, i) => {
      tl.fromTo(ls.el, { xPercent: ls.start }, { xPercent: 0, duration: 0.5, ease: 'power4.out' }, i * 0.07);
    });

    const lastTime = layerStates.length ? (layerStates.length - 1) * 0.07 : 0;
    const panelInsertTime = lastTime + (layerStates.length ? 0.08 : 0);
    const panelDuration = 0.65;

    tl.fromTo(
      panel,
      { xPercent: panelStart },
      { xPercent: 0, duration: panelDuration, ease: 'power4.out' },
      panelInsertTime
    );

    if (itemEls.length) {
      const itemsStartRatio = 0.15;
      const itemsStart = panelInsertTime + panelDuration * itemsStartRatio;

      tl.to(
        itemEls,
        { yPercent: 0, rotate: 0, duration: 1, ease: 'power4.out', stagger: { each: 0.1, from: 'start' } },
        itemsStart
      );

      if (numberEls.length) {
        tl.to(
          numberEls,
          { duration: 0.6, ease: 'power2.out', '--sm-num-opacity': 1, stagger: { each: 0.08, from: 'start' } },
          itemsStart + 0.1
        );
      }
    }

    if (socialTitle || socialLinks.length) {
      const socialsStart = panelInsertTime + panelDuration * 0.4;

      if (socialTitle) tl.to(socialTitle, { opacity: 1, duration: 0.5, ease: 'power2.out' }, socialsStart);
      if (socialLinks.length) {
        tl.to(
          socialLinks,
          {
            y: 0,
            opacity: 1,
            duration: 0.55,
            ease: 'power3.out',
            stagger: { each: 0.08, from: 'start' },
            onComplete: () => gsap.set(socialLinks, { clearProps: 'opacity' })
          },
          socialsStart + 0.04
        );
      }
    }

    if (formElements.length) {
      const formsStart = panelInsertTime + panelDuration * 0.3;
      tl.to(
        formElements,
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power3.out',
          stagger: { each: 0.08, from: 'start' },
          overwrite: true
        },
        formsStart
      );
    }

    openTlRef.current = tl;
    return tl;
  }, []);

  const playOpen = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    setOpen(true);
    onMenuOpen?.();

    const tl = buildOpenTimeline();
    if (tl) {
      tl.eventCallback('onComplete', () => {
        busyRef.current = false;
      });
      tl.play(0);
    } else {
      busyRef.current = false;
    }
  }, [buildOpenTimeline, onMenuOpen]);

  const playClose = useCallback(() => {
    openTlRef.current?.kill();
    openTlRef.current = null;

    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return;

    const all = [...layers, panel];
    closeTweenRef.current?.kill();

    const offscreen = 100;

    closeTweenRef.current = gsap.to(all, {
      xPercent: offscreen,
      duration: 0.32,
      ease: 'power3.in',
      overwrite: 'auto',
      onComplete: () => {
        const itemEls = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
        if (itemEls.length) gsap.set(itemEls, { yPercent: 140, rotate: 10 });

        const numberEls = Array.from(panel.querySelectorAll('.sm-panel-list[data-numbering] .sm-panel-item'));
        if (numberEls.length) gsap.set(numberEls, { '--sm-num-opacity': 0 });

        const socialTitle = panel.querySelector('.sm-socials-title');
        const socialLinks = Array.from(panel.querySelectorAll('.sm-socials-link'));
        if (socialTitle) gsap.set(socialTitle, { opacity: 0 });
        if (socialLinks.length) gsap.set(socialLinks, { y: 25, opacity: 0 });

        const formElements = Array.from(panel.querySelectorAll('.form-element'));
        if (formElements.length) gsap.set(formElements, { y: 30, opacity: 0 });

        setOpen(false);
        busyRef.current = false;
        onMenuClose?.();
      }
    });
  }, [onMenuClose]);

  // Auto-open when component mounts
  React.useEffect(() => {
    const timer = setTimeout(() => {
      playOpen();
    }, 50);
    return () => clearTimeout(timer);
  }, [playOpen]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;

      // Get user role from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', data.user.id)
        .single();

      if (userError) throw userError;

      // Redirect based on role
      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/portal');
      }

      playClose();
    } catch (error) {
      console.error('Sign in error:', error);
      setError(error.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');

    if (signUpData.password !== signUpData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signUpData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: {
          data: {
            full_name: signUpData.name,
            role: 'client'
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        setError('');
        alert('Account created! Please check your email to verify your account.');
        setIsSignUp(false);
        setSignUpData({ name: '', email: '', password: '', confirmPassword: '' });
      }
    } catch (error) {
      console.error('Sign up error:', error);
      setError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (isSignUp) {
      setSignUpData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="sm-scope z-40 fixed top-0 left-0 w-screen h-screen overflow-hidden">
      <div
        className="staggered-menu-wrapper relative w-full h-full"
        style={accentColor ? { '--sm-accent': accentColor } : undefined}
        data-position={position}
        data-open={open || undefined}
      >
        <div
          ref={preLayersRef}
          className="sm-prelayers absolute top-0 right-0 bottom-0 pointer-events-none z-[5]"
          aria-hidden="true"
        >
          {colors.map((c, i) => (
            <div
              key={i}
              className="sm-prelayer absolute top-0 right-0 h-full w-full translate-x-0"
              style={{ background: c, width: 'clamp(300px, 40vw, 500px)' }}
            />
          ))}
        </div>

        <aside
          id="staggered-menu-panel"
          ref={panelRef}
          className="staggered-menu-panel absolute top-0 right-0 h-full bg-white flex flex-col p-2 overflow-y-auto z-10 backdrop-blur-[12px]"
          style={{
            WebkitBackdropFilter: 'blur(12px)',
            width: 'clamp(300px, 40vw, 500px)'
          }}
          aria-hidden={!open}
        >
          {/* Close Button */}
          <button
            onClick={playClose}
            className="absolute top-4 right-4 flex items-center gap-2 px-4 py-3 text-black hover:text-gray-600 transition-colors duration-200 z-20 bg-transparent border-none cursor-pointer"
            aria-label="Close menu"
          >
            <span className="text-base font-medium">Close</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="sm-panel-inner flex-1 flex flex-col gap-3 mt-0">
            {/* Sign In Title Section */}
            <div className="menu-title">
              <p className="text-[#073A29] text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-[#073A29] rounded-full"></span>
                {isSignUp ? 'SIGN UP' : 'SIGN IN'}
              </p>
              <h2 className="text-4xl lg:text-5xl font-bold text-black mb-2">
                {isSignUp ? 'Create Account' : 'Welcome Back'}
              </h2>
              <p className="text-gray-600 text-base">
                {isSignUp
                  ? 'Create your account to start managing your projects.'
                  : 'Sign in to your account to access your dashboard and manage your projects.'}
              </p>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
            </div>

            {displaySocials && (
              <div className="flex flex-col gap-6">
                <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className="flex flex-col gap-2">
                  {isSignUp && (
                    <div className="form-element">
                      <label className="block text-sm font-semibold text-black mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={signUpData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm !bg-white"
                        placeholder="Your full name"
                      />
                    </div>
                  )}

                  <div className="form-element">
                    <label className="block text-sm font-semibold text-black mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={isSignUp ? signUpData.email : formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm !bg-white"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="form-element">
                    <label className="block text-sm font-semibold text-black mb-2">Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={isSignUp ? signUpData.password : formData.password}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm !bg-white"
                      placeholder="Your password"
                    />
                  </div>

                  {isSignUp && (
                    <div className="form-element">
                      <label className="block text-sm font-semibold text-black mb-2">Confirm Password *</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={signUpData.confirmPassword}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm !bg-white"
                        placeholder="Confirm your password"
                      />
                    </div>
                  )}

                  {!isSignUp && (
                    <div className="form-element flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input type="checkbox" className="rounded border-gray-400" />
                        Remember me
                      </label>
                      <button type="button" className="text-sm text-[#073A29] hover:text-[#073A29]/80 transition-colors bg-transparent border-none p-0 cursor-pointer">
                        Forgot password?
                      </button>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="form-element w-full bg-black text-white py-4 rounded-xl font-semibold hover:bg-[#073A29] transition-colors duration-200 text-sm flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
                    {!loading && (
                      <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    )}
                  </button>

                  <div className="form-element text-center">
                    <p className="text-gray-600 text-sm">
                      {isSignUp ? 'Already have an account?' : "Don't have a project?"}{' '}
                      {isSignUp ? (
                        <button
                          type="button"
                          onClick={() => setIsSignUp(false)}
                          className="text-[#073A29] hover:text-[#073A29]/80 font-semibold transition-colors bg-transparent border-none p-0 cursor-pointer"
                        >
                          Sign in
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            onContactUs?.();
                          }}
                          className="text-[#073A29] hover:text-[#073A29]/80 font-semibold transition-colors bg-transparent border-none p-0 cursor-pointer"
                        >
                          Contact us
                        </button>
                      )}
                    </p>
                  </div>

                  {isSignUp && (
                    <div className="text-center text-sm text-gray-600 form-element">
                      By signing up, you agree to our{' '}
                      <button type="button" className="text-[#073A29] hover:underline bg-transparent border-none p-0 cursor-pointer">
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button type="button" className="text-[#073A29] hover:underline bg-transparent border-none p-0 cursor-pointer">
                        Privacy Policy
                      </button>
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
        </aside>
      </div>

      <style>{`
        .sm-scope .staggered-menu-wrapper { position: relative; width: 100%; height: 100%; z-index: 40; }
        .sm-scope .sm-prelayers { position: absolute; top: 0; right: 0; bottom: 0; pointer-events: none; z-index: 5; }
        .sm-scope .sm-prelayer { position: absolute; top: 0; right: 0; height: 100%; width: 100%; transform: translateX(0); }
        .sm-scope .staggered-menu-panel { position: absolute; top: 0; right: 0; height: 100%; background: #ffffff !important; backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); display: flex; flex-direction: column; padding: 6em 2em 2em 2em; overflow-y: auto; z-index: 10; }
        .sm-scope .sm-panel-inner { flex: 1; display: flex; flex-direction: column; gap: 1.25rem; }
        .sm-scope .sm-socials { margin-top: auto; padding-top: 2rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .sm-scope .sm-socials-title { margin: 0; font-size: 1rem; font-weight: 500; color: var(--sm-accent, #ff0000); }
        .sm-scope .sm-socials-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: row; align-items: center; gap: 1rem; flex-wrap: wrap; }
        .sm-scope .sm-socials-list .sm-socials-link { opacity: 1; transition: opacity 0.3s ease; }
        .sm-scope .sm-socials-list:hover .sm-socials-link:not(:hover) { opacity: 0.35; }
        .sm-scope .sm-socials-list:focus-within .sm-socials-link:not(:focus-visible) { opacity: 0.35; }
        .sm-scope .sm-socials-list .sm-socials-link:hover,
        .sm-scope .sm-socials-list .sm-socials-link:focus-visible { opacity: 1; }
        .sm-scope .sm-socials-link:focus-visible { outline: 2px solid var(--sm-accent, #ff0000); outline-offset: 3px; }
        .sm-scope .sm-socials-link { font-size: 1.2rem; font-weight: 500; color: #111; text-decoration: none; position: relative; padding: 2px 0; display: inline-block; transition: color 0.3s ease, opacity 0.3s ease; }
        .sm-scope .sm-socials-link:hover { color: var(--sm-accent, #ff0000); }
        .sm-scope .sm-panel-title { margin: 0; font-size: 1rem; font-weight: 600; color: #fff; text-transform: uppercase; }
        .sm-scope .sm-panel-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
        .sm-scope .sm-panel-item { position: relative; color: #000; font-weight: 600; font-size: 4rem; cursor: pointer; line-height: 1; letter-spacing: -2px; text-transform: uppercase; transition: background 0.25s, color 0.25s; display: inline-block; text-decoration: none; padding-right: 1.4em; }
        .sm-scope .sm-panel-itemLabel { display: inline-block; will-change: transform; transform-origin: 50% 100%; }
        .sm-scope .sm-panel-item:hover { color: var(--sm-accent, #ff0000); }
        .sm-scope .sm-panel-list[data-numbering] { counter-reset: smItem; }
        .sm-scope .sm-panel-list[data-numbering] .sm-panel-item::after { counter-increment: smItem; content: counter(smItem, decimal-leading-zero); position: absolute; top: 0.1em; right: 3.2em; font-size: 18px; font-weight: 400; color: var(--sm-accent, #ff0000); letter-spacing: 0; pointer-events: none; user-select: none; opacity: var(--sm-num-opacity, 0); }
      `}</style>
    </div>
  );
};

export default SignInStaggeredMenu;